// Fitting-name resolution: canonical (US) short name -> store-specific
// Scheduling_App_Name__c.
//
// Salesforce has a separate Product2 record per country because pricing,
// duration, and availability rules can vary by market. That means the wire
// name the widget must send on Availability / Schedule / Waitlist differs
// per store — "Wedge" in Chicago, "Wedge UK" in Glasgow, "Wedge Canada" in
// Toronto, "Wedge AUS" in Sydney.
//
// The widget's fittingTypes.ts continues to use canonical US short names as
// the UI-and-code contract; this module resolves those against the map the
// middleware returns from /fittings?storeName=<>.

/**
 * Names that appear at the end of the wire string identifying the country.
 * Ordering matters when longer variants ("Australia") share a prefix with
 * shorter ones ("AUS") — put longest first so the regex peels the biggest
 * match. Case-insensitive.
 *
 * The separator is optional and may be a hyphen or a dash. Products have
 * historically been named "Wedge UK", but the September 2026 Iron Shaft
 * Challenge arrived as "Iron Shaft Challenge - UK". Without tolerating that,
 * the suffix peels to "Iron Shaft Challenge -" — trailing hyphen and all — the
 * canonical lookup misses, the widget sends the bare US name to a UK store, and
 * /Availability answers with an empty array rather than an error. The customer
 * sees "no days available" on a calendar that is open. That is the same silent
 * failure the Australian iron fitting hid behind, which is why this tolerates
 * the separator rather than trusting every future product to be named
 * consistently.
 */
const COUNTRY_SUFFIX_RE = /\s+(?:[-\u2013\u2014]\s*)?(Australia|Canada|CDN|AUS|USA|UK|US)$/i;

/**
 * Peel the trailing country token off a wire name so it can be indexed by
 * the widget's canonical US short name. `"Wedge UK"` → `"Wedge"`, `"Full Bag"`
 * → `"Full Bag"` (unchanged). Leaves multi-word canonical names intact
 * ("Callaway Quantum Leap Challenge" stays as-is).
 */
export function stripCountrySuffix(name: string): string {
  return name.replace(COUNTRY_SUFFIX_RE, '').trim();
}

/**
 * Wire names for a fitting the widget knows under a different canonical name.
 *
 * Stripping the country suffix assumes every market sells the same product
 * under the same base name. Australia does not: its iron fitting is
 * `"Irons & Wedge AUS"`, which strips to `"Irons & Wedge"` and so never
 * matched the widget's canonical `"Iron"`. The widget fell back to sending
 * `"Iron"`, Salesforce has no Australian product by that name, and the slot
 * grid came back empty for every Australian store — reported by CC as
 * "Iron + Wedge service days hours still not showing".
 *
 * Verified 2026-08-26 against production: US `"Iron"`, UK `"Iron UK"` and
 * Canada `"Iron Canada"` all strip to `"Iron"` correctly. Australia was the
 * only market that renamed the product rather than suffixing it.
 *
 * Keyed on the country-stripped wire name, normalized by `aliasKey`. Add an
 * entry here when a market names a product differently rather than trying to
 * make `stripCountrySuffix` cleverer — the suffix rule is about countries, and
 * this is about product naming.
 */
const CANONICAL_ALIASES: Record<string, string> = {
  'irons & wedge': 'Iron',
};

/** Lowercase and collapse whitespace, so alias lookup survives SF's spacing. */
function aliasKey(name: string): string {
  return name.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Build a canonical-name → wire-name map from the middleware's /fittings
 * response. Duplicate canonicals (shouldn't happen at a single store) keep
 * the first entry.
 */
export function buildFittingNameMap(
  fittings: Array<{ schedulingAppName: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fittings) {
    const wire = f.schedulingAppName?.trim();
    if (!wire) continue;
    const stripped = stripCountrySuffix(wire);
    const canonical = CANONICAL_ALIASES[aliasKey(stripped)] ?? stripped;
    if (!out[canonical]) out[canonical] = wire;
  }
  return out;
}

/**
 * Resolve a canonical US short name into the store-specific wire name.
 * Falls back to the canonical name when no map is available (widget booted
 * without visiting a store, or the /fittings call failed) — preserves US
 * behavior instead of hard-failing.
 */
export function resolveWireName(
  canonicalName: string,
  map: Record<string, string> | null | undefined,
): string {
  if (!map) return canonicalName;
  const wire = map[canonicalName];
  if (wire) return wire;

  // Case-insensitive second pass. Salesforce product names are typed by hand
  // and their casing is not guaranteed to match the widget's canonical
  // spelling — "IRON SHAFT CHALLENGE UK" strips to "IRON SHAFT CHALLENGE",
  // which an exact lookup for "Iron Shaft Challenge" misses. The consequence
  // is invisible: the bare US name goes to an international store, SF answers
  // with an empty array rather than an error, and the customer sees "no days
  // available" on an open calendar. Not worth a silent failure over letter
  // case.
  const wanted = canonicalName.toLowerCase().replace(/\s+/g, ' ').trim();
  for (const [key, value] of Object.entries(map)) {
    if (key.toLowerCase().replace(/\s+/g, ' ').trim() === wanted) return value;
  }

  // Falling back while holding a map is the failure that hid the Australian
  // bug: the widget sends a name the store's Salesforce org does not stock,
  // /Availability returns an empty array rather than an error, and the grid
  // renders as "no days available" — indistinguishable from a genuinely full
  // calendar. Say so, so the next mismatch is one console line rather than a
  // round of client testing.
  //
  // Deliberately not thrown. A missing entry for one fitting must not take
  // down a store's whole booking flow, and the US path — where map and
  // canonical agree — is unaffected either way.
  if (typeof console !== 'undefined') {
    console.warn(
      `[cc-widget] No wire name for fitting "${canonicalName}" at this store. ` +
        `Sending it unchanged; availability will be empty if Salesforce does not ` +
        `stock that name. Known here: ${Object.keys(map).join(', ') || '(none)'}`,
    );
  }
  return canonicalName;
}

/** Wire fitting name → the studio's list price, from a /fittings answer. */
export function buildFittingPriceMap(
  fittings: Array<{ schedulingAppName?: string; unitPrice?: number | string }>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of fittings) {
    const wire = f.schedulingAppName?.trim();
    const price = Number(f.unitPrice ?? 0);
    if (wire && price > 0) out[wire] = price;
  }
  return out;
}

/**
 * The price to send to /promos/validate for a fitting: the studio's list
 * price when known, else the market label's first amount. Salesforce echoes
 * the sent price back as `originalValue` and the applied line prints it as
 * "(was $X)", so the label's with-purchase figure produced "now $0 (was
 * $50)" for a $175 Driver.
 */
export function listPriceFor(
  wireName: string,
  prices: Record<string, number> | null,
  labelPrice: number,
): number {
  const p = prices?.[wireName];
  return typeof p === 'number' && p > 0 ? p : labelPrice;
}
